import { AntDesignOutlined, BulbOutlined, CopyOutlined, InfoCircleOutlined, RedoOutlined, UserOutlined } from "@ant-design/icons";
import { Actions, Bubble, Prompts, PromptsProps, Sender } from "@ant-design/x";
import { Avatar, Flex, message, Tooltip } from "antd";
import React, { useCallback, useMemo, useRef, useState } from "react";
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { items } from "./Constant";
import { useMediaQuery } from "@uidotdev/usehooks";


const actionItems = (content: string) => [
    {
        key: 'copy',
        label: 'copy',
        actionRender: () => {
            return <Actions.Copy text={content} />;
        },
    },
    {
        key: 'retry',
        icon: <RedoOutlined />,
        label: 'Retry',
    },
];



export const AIAssistant = ({ threadId, response, setResponse }: { threadId: string, response: { type: string, text: string }[], setResponse: (res: { type: string, text: string }[]) => void }) => {
    const [value, setValue] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const isMobile = useMediaQuery('(max-width: 600px)');

    const [promptsVisible, setPromptsVisible] = useState(false);

    const ref = useRef<HTMLDivElement>(null);

    const scrollDir = React.useRef<'down' | 'up'>('down');
    const prevY = React.useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);


    React.useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            scrollDir.current = y > prevY.current ? 'down' : 'up';
            prevY.current = y;
        };

        const observer = new IntersectionObserver(
            ([entry], observer) => {
                // 只在向下滚动时，并且元素接近视口底部时显示 prompts
                if (entry.isIntersecting && scrollDir.current === 'down') {
                    const top = entry.boundingClientRect.top; // 相对于视口顶部
                    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                    const distanceFromBottom = viewportHeight - top; // 元素顶部距离视口底部
                    if (distanceFromBottom >= 0 && distanceFromBottom <= 140) {
                        setPromptsVisible(true);
                        return;
                    }
                }
                setPromptsVisible(false);
            },
            {
                // 把 rootMargin 调成更合理的值；我们用 boundingClientRect 检查具体距离
                rootMargin: '0px 0px 0px 0px',
                threshold: 0,
            }
        );

        window.addEventListener('scroll', onScroll, { passive: true });
        if (ref.current) observer.observe(ref.current);

        return () => {
            window.removeEventListener('scroll', onScroll);
            observer.disconnect();
        };
    }, []);

    React.useEffect(() => {
        loading && setValue('');
    }, [loading])

    const onSubmit = useCallback(async (value: string) => {

        if (!value || value.trim() === '') {
            message.error('Please enter a question');
            return;
        }
        try {
            const envUrl = process.env.REACT_APP_YASI_ASSISTANT_URL || process.env.YASI_ASSISTANT_URL;
            const baseUrl = envUrl ? String(envUrl).replace(/\/+$/, '') : '/api/ai';
            setResponse([...response, { type: 'human', text: value }]);
            setLoading(true);
            const res = await axios.post(`${baseUrl}/invoke`, { input: value, thread_id: threadId });
            const newResponse = res.data ? [...response, { type: 'human', text: value }, res.data.messages[res.data.messages.length - 1]] : response
            setResponse(newResponse);
            setLoading(false);
            message.success('Received response');
        } catch (err) {
            console.error(err);
            message.error('Request failed');
        } finally {
            setLoading(false);
            setValue('');
        }

    }, [value, threadId])


    return (<div style={{ padding: 20, paddingBottom: 140, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <h1>Find a word with AI</h1>
        <div style={{
            width: isMobile ? '90vw' : '50vw', display: 'flex', flexDirection: 'column', alignItems: 'space-between'
        }}>
            <Flex vertical gap="small" style={{ margin: '40px 0 250px  0' }}>
                {

                    response?.map(message => (
                        <Bubble
                            placement={message.type === 'ai' ? 'start' : 'end'}
                            typing={{
                                effect: message.type === 'ai' ? 'typing' : null,
                                interval: 50,
                                step: 3,
                                keepPrefix: true,
                            }}
                            header={message.type === 'ai' && <h6>Pupu</h6>}
                            content={<ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{message.text}</ReactMarkdown>}
                            avatar={
                                message.type === 'ai' ? <Avatar src="/pugongying.jpg" /> : <Avatar icon={<UserOutlined />} />
                            }
                            style={{ marginBottom: '5px' }}
                        />
                    ))
                }
                {loading && <Bubble
                    loading={true}
                    content=""
                    placement={'start'}
                    typing={{
                        effect: 'typing',
                        interval: 50,
                        step: 3,
                        keepPrefix: true,
                    }}
                    header={<h6>Pupu</h6>}
                    avatar={
                        <Avatar src="/pugongying.jpg" />
                    }
                    style={{ marginBottom: '5px' }}
                />}
                <div ref={ref} style={{ height: 0 }} />
            </Flex>

            <div style={{
                position: 'fixed',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                width: isMobile ? '90vw' : '50vw',
                zIndex: 1200,
                borderRadius: 12,
                padding: 12,

            }}>

                {promptsVisible && !value && <Prompts
                    key={0}
                    fadeIn={false}
                    fadeInLeft={true}
                    title="✨ Inspirational Sparks and Marvelous Tips"
                    items={items}
                    onItemClick={(info) => {
                        setValue(items[info.data.key]['description'])
                    }}
                    style={{
                        marginBottom: 30,
                        opacity: promptsVisible ? 1 : 0,
                        transform: promptsVisible ? 'translateY(0)' : 'translateY(8px)',
                        transition: 'opacity .35s ease, transform .35s ease',
                        pointerEvents: promptsVisible ? 'auto' : 'none',
                    }}
                />}
                <Sender
                    loading={loading}
                    value={value}
                    placeholder="Input your question"
                    onChange={(v) => {
                        setValue(v);
                    }}
                    onSubmit={onSubmit}
                    onCancel={() => {
                        setLoading(false);
                        message.error('Cancel sending!');
                    }}
                    autoSize={{ minRows: 4, maxRows: 6 }}
                    style={{
                        background: '#ffffff',
                        borderRadius: 12,
                        padding: 12,
                        boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                        border: '1px solid rgba(0,0,0,0.06)'
                    }}
                />
            </div>

        </div>

    </div >)
}
